import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { useCanOrganizationWide } from "@/lib/policy";
import {
  ScreeningTemplateForm,
  screeningTemplateToInput,
  toScreeningQuestions,
} from "./-components/screening-template-form";
import {
  ScreeningTemplateList,
  type ScreeningTemplateRow,
} from "./-components/screening-template-list";

export const Route = createFileRoute("/_authenticated/_workforce/hiring/screening-templates")({
  component: ScreeningTemplatesPage,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** The reusable question sets a job asks as somebody applies. These are the only questions that are
 * graded, and the only ones that can end an application. */
function ScreeningTemplatesPage() {
  const canManage = useCanOrganizationWide("hiring:manage");
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ScreeningTemplateRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ScreeningTemplateRow | null>(null);

  const templates = useQuery(
    orpc.workforce.hiringTemplates.screening.list.queryOptions({ input: {} }),
  );
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.workforce.hiringTemplates.screening.key() });

  const create = useMutation(
    orpc.workforce.hiringTemplates.screening.create.mutationOptions({
      onSuccess: async () => {
        toast.success("Template created");
        setCreating(false);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const update = useMutation(
    orpc.workforce.hiringTemplates.screening.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Template updated");
        setEditing(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const remove = useMutation(
    orpc.workforce.hiringTemplates.screening.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Template deleted");
        setDeleteTarget(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  const rows = templates.data ?? [];
  const active = rows.filter((row) => row.isActive).length;

  return (
    <>
      <PageHeader
        title="Screening questions"
        meta={
          rows.length > 0
            ? `${rows.length} defined, ${active} in use. Asked on the job ad as somebody applies. These are the only questions that are graded, and the only ones that can end an application.`
            : "Questions asked as somebody applies. The only graded ones."
        }
        actions={canManage ? <Button onClick={() => setCreating(true)}>New template</Button> : null}
      />

      {templates.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : templates.isError ? (
        <ReadFailed
          message="Could not load screening templates."
          error={templates.error}
          onRetry={() => void templates.refetch()}
          retrying={templates.isFetching}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No screening templates yet"
          description="Create one to reuse the same questions across postings and grade applicants consistently."
        />
      ) : (
        <ScreeningTemplateList
          templates={rows}
          canManage={canManage}
          onEdit={setEditing}
          onDelete={setDeleteTarget}
        />
      )}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>New screening template</DialogTitle>
          </DialogHeader>
          <ScreeningTemplateForm
            pending={create.isPending}
            submitLabel="Create"
            onCancel={() => setCreating(false)}
            onSubmit={(values) => create.mutate(screeningTemplateToInput(values))}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit screening template</DialogTitle>
          </DialogHeader>
          {editing ? (
            <ScreeningTemplateForm
              pending={update.isPending}
              submitLabel="Save changes"
              defaultValues={{
                name: editing.name,
                description: editing.description ?? "",
                isActive: editing.isActive,
                fields: toScreeningQuestions(editing.fields),
              }}
              onCancel={() => setEditing(null)}
              onSubmit={(values) =>
                update.mutate({
                  screeningTemplateId: editing.id,
                  ...screeningTemplateToInput(values),
                })
              }
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this template?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes &ldquo;{deleteTarget?.name}&rdquo;. Jobs that referenced it keep their
              own inline questions; they simply stop using this template.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() =>
                deleteTarget && remove.mutate({ screeningTemplateId: deleteTarget.id })
              }
            >
              {remove.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
